// Upload queue: files go straight to S3 with presigned URLs. Over 100 MB a file goes in 64 MB parts (S3 multipart);
// each part retries with backoff and fresh URLs, so a dropped connection or sleeping laptop resumes where it stopped.
import { useSyncExternalStore } from "react";
import { api, ApiError } from "./api";

export interface UploadItem {
  id: string;
  name: string;
  path: string;
  size: number;
  loaded: number;
  status: "queued" | "uploading" | "done" | "failed" | "cancelled";
  error?: string;
}

const FILE_CONCURRENCY = 3;
const PART_CONCURRENCY = 4;
const RETRIES = 6;

let items: UploadItem[] = [];
const files = new Map<string, File>();
const aborts = new Map<string, () => void>();
const listeners = new Set<() => void>();
let onFinished: (() => void) | null = null;
let active = 0;

const emit = () => {
  items = [...items];
  listeners.forEach((l) => l());
};
const update = (id: string, patch: Partial<UploadItem>) => {
  items = items.map((i) => (i.id === id ? { ...i, ...patch } : i));
  listeners.forEach((l) => l());
};

export function useUploads() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => items,
  );
}

/** Called after each finished upload (the file list refreshes). */
export const setUploadFinished = (fn: (() => void) | null) => (onFinished = fn);

export function enqueue(list: { file: File; path: string }[]) {
  for (const { file, path } of list) {
    const id = crypto.randomUUID();
    files.set(id, file);
    items.push({ id, name: file.name, path, size: file.size, loaded: 0, status: "queued" });
  }
  emit();
  pump();
}

export function cancel(id: string) {
  aborts.get(id)?.();
  const it = items.find((i) => i.id === id);
  if (it && (it.status === "queued" || it.status === "uploading")) update(id, { status: "cancelled" });
}

export function retry(id: string) {
  update(id, { status: "queued", loaded: 0, error: undefined });
  pump();
}

export function clearFinished() {
  items = items.filter((i) => i.status === "queued" || i.status === "uploading");
  for (const id of [...files.keys()]) if (!items.some((i) => i.id === id)) files.delete(id);
  emit();
}

function pump() {
  while (active < FILE_CONCURRENCY) {
    const next = items.find((i) => i.status === "queued");
    if (!next) return;
    active++;
    update(next.id, { status: "uploading" });
    void run(next.id).finally(() => {
      active--;
      pump();
    });
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** PUT with progress. Resolves with the ETag header. */
function put(url: string, body: Blob, headers: Record<string, string>, onProgress: (n: number) => void, signal: { abort?: () => void }): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve(xhr.getResponseHeader("ETag") ?? "") : reject(Object.assign(new Error(`Upload failed (${xhr.status})`), { http: xhr.status })));
    xhr.onerror = () => reject(Object.assign(new Error("Network error"), { http: 0 }));
    xhr.onabort = () => reject(Object.assign(new Error("Cancelled"), { cancelled: true }));
    signal.abort = () => xhr.abort();
    xhr.send(body);
  });
}

async function run(id: string) {
  const file = files.get(id)!;
  const item = items.find((i) => i.id === id)!;
  let cancelled = false;
  const live = new Set<{ abort?: () => void }>();
  aborts.set(id, () => {
    cancelled = true;
    live.forEach((s) => s.abort?.());
  });
  let uploadId: string | undefined;
  try {
    const start = await api<{ mode: "single" | "multipart"; url?: string; contentType?: string; uploadId?: string; partBytes?: number; partCount?: number }>("/uploads", {
      body: { path: item.path, size: file.size, contentType: file.type || "application/octet-stream" },
    });
    if (cancelled) throw Object.assign(new Error("Cancelled"), { cancelled: true });
    if (start.mode === "single") {
      for (let attempt = 0; ; attempt++) {
        const sig = {};
        live.add(sig);
        try {
          await put(start.url!, file, { "Content-Type": start.contentType! }, (n) => update(id, { loaded: n }), sig);
          break;
        } catch (e: any) {
          if (e.cancelled || attempt >= RETRIES) throw e;
          await sleep(1000 * 2 ** attempt);
        } finally {
          live.delete(sig);
        }
      }
      await api("/uploads/complete", { body: { path: item.path } });
    } else {
      uploadId = start.uploadId!;
      const partBytes = start.partBytes!;
      const total = start.partCount!;
      const loaded = new Map<number, number>();
      const etags = new Map<number, string>();
      const urls = new Map<number, string>();
      const report = () => update(id, { loaded: [...loaded.values()].reduce((a, b) => a + b, 0) });
      const fetchUrls = async (numbers: number[]) => {
        const r = await api<{ urls: Record<string, string> }>("/uploads/parts", { body: { path: item.path, size: file.size, uploadId, partNumbers: numbers } });
        for (const [n, u] of Object.entries(r.urls)) urls.set(Number(n), u);
      };
      let nextPart = 1;
      const worker = async () => {
        while (!cancelled && nextPart <= total) {
          const n = nextPart++;
          const blob = file.slice((n - 1) * partBytes, Math.min(n * partBytes, file.size));
          for (let attempt = 0; ; attempt++) {
            if (!urls.has(n)) await fetchUrls(Array.from({ length: Math.min(8, total - n + 1) }, (_, i) => n + i).filter((k) => !etags.has(k)));
            const sig = {};
            live.add(sig);
            try {
              etags.set(n, await put(urls.get(n)!, blob, {}, (b) => (loaded.set(n, b), report()), sig));
              loaded.set(n, blob.size);
              report();
              break;
            } catch (e: any) {
              if (e.cancelled || attempt >= RETRIES) throw e;
              loaded.set(n, 0);
              urls.delete(n); // expired or bad URL: sign a fresh one
              await sleep(Math.min(30_000, 1000 * 2 ** attempt));
            } finally {
              live.delete(sig);
            }
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, total) }, worker));
      if (cancelled) throw Object.assign(new Error("Cancelled"), { cancelled: true });
      await api("/uploads/complete", { body: { path: item.path, uploadId, parts: [...etags].map(([partNumber, etag]) => ({ partNumber, etag })) } });
    }
    update(id, { status: "done", loaded: file.size });
    files.delete(id);
    onFinished?.();
  } catch (e: any) {
    if (uploadId) void api("/uploads/abort", { body: { path: item.path, uploadId } }).catch(() => undefined);
    if (e.cancelled || cancelled) update(id, { status: "cancelled" });
    else update(id, { status: "failed", error: e instanceof ApiError ? e.message : e.message || "Upload failed" });
  } finally {
    aborts.delete(id);
  }
}

// ---------- picking files and folders ----------

/** Files from an <input type=file> (with or without webkitdirectory), placed under `folder`. */
export function fromInput(list: FileList, folder: string) {
  return [...list].map((file) => ({ file, path: folder + ((file as any).webkitRelativePath || file.name) }));
}

/** Files from a drop, walking dropped folders (keeps the structure). */
export async function fromDrop(dt: DataTransfer, folder: string): Promise<{ file: File; path: string }[]> {
  const entries = [...dt.items].map((i) => i.webkitGetAsEntry?.()).filter(Boolean) as FileSystemEntry[];
  if (!entries.length) return [...dt.files].map((file) => ({ file, path: folder + file.name }));
  const out: { file: File; path: string }[] = [];
  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
      out.push({ file, path: folder + prefix + file.name });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (!batch.length) break;
        for (const e of batch) await walk(e, `${prefix}${entry.name}/`);
      }
    }
  };
  for (const e of entries) await walk(e, "");
  return out;
}
